import { _buildQueryUrl, deleteRequest, get, post, postJSON, put, putJSON } from '../utils/Request';
import type {
  AccountIdentityVerificationCodeBody,
  AccountQuota,
  AddonQuotaBalance,
  BindAccountIdentityBody,
  ChangeAccountIdentityBody,
  CurrentAccount,
  UnbindAccountIdentityBody,
  VisibleMenu
} from '../types/account';
import type { CatalogPlanQuery, EntitlementPackageOffer, PlanOffer } from '../types/catalog';
import type { PurchasedOrderPage, PurchasedOrderQuery } from '../types/order';
import type { CreatePurchaseOrderBody, PaymentResult, PurchaseOrderContent } from '../types/purchase';
import type { CreatorProductCategory } from '../types/category';
import type {
  InfluencerBlacklistAddBody,
  InfluencerBlacklistPage,
  InfluencerBlacklistPageBody,
  InfluencerBlacklistPageQuery,
  InfluencerBlacklistQuery,
  InfluencerCollectionAddBody,
  InfluencerCollectionAddQuery,
  InfluencerCollectionBody,
  InfluencerCollectionPage,
  InfluencerCollectionQuery,
  InfluencerCooperationBody,
  InfluencerCooperationPage,
  InfluencerCooperationQuery,
  InfluencerDetail,
  InfluencerFunnel,
  InfluencerFunnelQuery,
  InfluencerIntentionBody,
  InfluencerIntentionPage,
  InfluencerIntentionQuery,
  IntentionToCooperationQuery,
  InfluencerPage,
  InfluencerPageBody,
  InfluencerPageQuery
} from '../types/influencer';
import type {
  AddCollaborationCreatorsBody,
  AddCollaborationCreatorsQuery,
  RemoveCollaborationCreatorsBody,
  CancelCollaborationsBody,
  MergeCollaborationsBody,
  MergeCollaborationsQuery,
  TargetCollaborationMergeResult,
  AddCollaborationProductsBody,
  CollaborationBatchResult,
  CollaborationPageQuery,
  TargetCollaborationDetail,
  TargetCollaborationPage,
  CollaborationDetailQuery,
  CreateCollaborationBody,
  TargetCollaborationCreateResult
} from '../types/collaboration';
import type { CommerceProductsQuery, ShopProductPage } from '../types/commerce';
import type { SellerAuthLink, SellerAuthShop, UnbindSellerShopBody, UnbindSellerShopQuery } from '../types/seller';
import type {
  CreateTemplateBody,
  CreateTemplateQuery,
  DeleteTemplateQuery,
  GenerateTemplateBody,
  GenerateTemplateQuery,
  GeneratedContentTemplate,
  TemplateDetail,
  TemplateDetailQuery,
  TemplateListQuery,
  TemplatePage,
  UpdateTemplateBody,
  UpdateTemplateQuery
} from '../types/template';
import type { CreateEmailCampaignBody, EmailCampaign } from '../types/emailCampaign';
import type {
  EmailConversationDetail,
  EmailConversationDetailQuery,
  EmailConversationListQuery,
  EmailConversationPage,
  EmailInboxStats,
  EmailPendingReplyCountQuery,
  EmailSendResult,
  EmailStatsQuery,
  ReplyEmailBody,
  ReplyEmailQuery
} from '../types/email';
import type {
  CreateDmConversationBody,
  CreateDmConversationQuery,
  DmConversation,
  DmConversationListQuery,
  DmConversationPage,
  DmPendingReplyCountQuery,
  DmMessageListQuery,
  DmMessagePage,
  StarDmConversationQuery,
  ArchiveDmConversationQuery,
  ReadDmConversationQuery,
  SendDmMessageBody,
  SendDmMessageQuery,
  DmSendResult,
  DmCampaignListQuery,
  DmCampaignPage,
  CreateDmCampaignBody,
  CreateDmCampaignQuery,
  DmCampaignCreated
} from '../types/dm';

/** 页面接口：工作台、店铺授权、个人中心等。 */

/** 查询当前额度 */
export function getAccountQuota() {
  return get<AccountQuota>({ path: '/api/v1/account/quota' });
}

/** 查询本人加量包额度。仅当前登录账号，无需传用户或店铺。返回体原样交给页面。 */
export function getAccountAddonQuota() {
  return get<AddonQuotaBalance>({ path: '/api/v1/account/quota/addons' });
}

/** 查询当前用户信息。返回体原样交给页面。 */
export function getCurrentAccount() {
  return get<CurrentAccount>({ path: '/api/v1/account/current' });
}

/** 查询当前账号可见菜单树。返回体原样交给页面。 */
export function getAccountMenus() {
  return get<VisibleMenu[]>({ path: '/api/v1/account/menus' });
}

/** 向指定联系方式发送账号绑定类验证码（绑定 / 换绑 / 解绑）。返回体原样交给页面。 */
export function sendAccountIdentityVerificationCode(body: AccountIdentityVerificationCodeBody) {
  return postJSON<null>({
    path: '/api/v1/account/identity/verification-code',
    params: body
  });
}

/** 绑定手机号或登录邮箱。需先向该联系方式发送 BINDING 验证码。返回体原样交给页面。 */
export function bindAccountIdentity(body: BindAccountIdentityBody) {
  return postJSON<null>({
    path: '/api/v1/account/identity/bind',
    params: body
  });
}

/** 解绑手机号或登录邮箱。需先向该联系方式发送 BINDING 验证码。返回体原样交给页面。 */
export function unbindAccountIdentity(body: UnbindAccountIdentityBody) {
  return postJSON<null>({
    path: '/api/v1/account/identity/unbind',
    params: body
  });
}

/** 换绑手机号或登录邮箱。旧、新联系方式均需各自的 BINDING 验证码。返回体原样交给页面。 */
export function changeAccountIdentity(body: ChangeAccountIdentityBody) {
  return postJSON<null>({
    path: '/api/v1/account/identity/change',
    params: body
  });
}

/** 查询当前地区可购买的独立权益包。返回体原样交给页面。 */
export function getCatalogEntitlementPackages() {
  return get<EntitlementPackageOffer[]>({
    path: '/api/v1/catalog/entitlement-packages'
  });
}

/** 分页查询当前地区可展示套餐。pageNum、pageSize 必填。返回体原样交给页面。 */
export function getCatalogPlans(query: CatalogPlanQuery) {
  return get<PlanOffer[]>({
    path: '/api/v1/catalog/plans',
    params: query
  });
}

/** 分页查询本人已支付订单，服务端按支付时间倒序。pageNum、pageSize 作为查询参数。返回体原样交给页面。 */
export function getPurchasedOrders(query: PurchasedOrderQuery = {}) {
  return get<PurchasedOrderPage>({
    path: '/api/v1/orders',
    params: query
  });
}

/** 下单并生成支付链接。region 头必填两位大写，当前约定传 CN。返回体原样交给页面。 */
export function createPurchaseOrder(body: CreatePurchaseOrderBody) {
  return postJSON<PurchaseOrderContent>({
    path: '/api/v1/orders',
    params: body,
    headers: { region: 'CN' }
  });
}

/** 查询本次订单支付结果。SUCCEEDED、FAILED、CLOSED 时停止轮询。返回体原样交给页面。 */
export function getOrderPaymentResult(orderNo: string) {
  return get<PaymentResult>({
    path: `/api/v1/orders/${encodeURIComponent(orderNo)}/payment-result`
  });
}

/** 查询已授权店铺。user_code 必填，其余查询参数按需传入。返回体原样交给页面。 */
export function getSellerShops(query: {
  user_code: string;
  userId?: string;
  sessionId?: string;
  sessionVersion?: number;
}) {
  return get<SellerAuthShop[]>({
    path: '/api/v1/auth/seller/shops',
    params: query
  });
}

/** 查询建联漏斗。shopId 必填，其余查询参数按需传入。返回体原样交给页面。 */
export function getInfluencerFunnel(query: InfluencerFunnelQuery) {
  return get<InfluencerFunnel>({
    path: '/api/v1/profile/influencer/funnel',
    params: query
  });
}

/** 收藏达人。influencerIds 为勾选的达人 ID 列表；不传 label 时由服务端按无标签保存。返回体原样交给页面。 */
export function addInfluencerCollection(
  body: InfluencerCollectionAddBody,
  query: InfluencerCollectionAddQuery = {}
) {
  return postJSON<boolean>({
    path: _buildQueryUrl('/api/v1/profile/influencer/collection/add', query),
    params: body
  });
}

/** 将达人加入黑名单。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function addInfluencerBlacklist(
  body: InfluencerBlacklistAddBody,
  query: InfluencerBlacklistQuery = {}
) {
  return postJSON<boolean>({
    path: _buildQueryUrl('/api/v1/profile/influencer/blacklist/add', query),
    params: body
  });
}

/** 分页查询达人黑名单。不传 deleted 仅查在黑名单，deleted 为 1 时查移出历史。返回体原样交给页面。 */
export function getInfluencerBlacklistPage(
  body: InfluencerBlacklistPageBody,
  query: InfluencerBlacklistPageQuery = {}
) {
  return postJSON<InfluencerBlacklistPage>({
    path: _buildQueryUrl('/api/v1/profile/influencer/blacklist/page', query),
    params: body
  });
}

/** 将达人移出黑名单。路径参数为达人 influencerId。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function cancelInfluencerBlacklist(
  influencerId: string,
  query: InfluencerBlacklistQuery = {}
) {
  return deleteRequest<boolean>({
    path: _buildQueryUrl(
      `/api/v1/profile/influencer/blacklist/cancel/${encodeURIComponent(influencerId)}`,
      query
    )
  });
}

/** 取消收藏达人。路径参数为达人 influencerId。返回体原样交给页面。 */
export function cancelInfluencerCollection(influencerId: string) {
  return deleteRequest<null>({
    path: `/api/v1/profile/influencer/collection/cancel/${encodeURIComponent(influencerId)}`
  });
}

/** 把意向达人转为合作。shopId 必填，会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function convertInfluencerIntention(influencerId: string, query: IntentionToCooperationQuery) {
  return post<boolean>({
    path: _buildQueryUrl(
      `/api/v1/profile/influencer/intention/toCooperation/${encodeURIComponent(influencerId)}`,
      query
    )
  });
}

/** 分页查询合作达人。筛选放在 JSON 请求体，会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function getInfluencerCooperationPage(
  body: InfluencerCooperationBody,
  query: InfluencerCooperationQuery = {}
) {
  return postJSON<InfluencerCooperationPage>({
    path: _buildQueryUrl('/api/v1/profile/influencer/cooperation/page', query),
    params: body
  });
}

/** 分页查询意向达人。筛选放在 JSON 请求体，会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function getInfluencerIntentionPage(
  body: InfluencerIntentionBody,
  query: InfluencerIntentionQuery = {}
) {
  return postJSON<InfluencerIntentionPage>({
    path: _buildQueryUrl('/api/v1/profile/influencer/intention/page', query),
    params: body
  });
}

/** 分页查询我的达人。需登录；筛选放在 JSON 请求体，会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function getInfluencerCollectionPage(
  body: InfluencerCollectionBody,
  query: InfluencerCollectionQuery = {}
) {
  return postJSON<InfluencerCollectionPage>({
    path: _buildQueryUrl('/api/v1/profile/influencer/collection/page', query),
    params: body
  });
}

/** 查询达人详情。路径参数为达人 influencerId。返回体原样交给页面。 */
export function getInfluencerDetail(influencerId: string) {
  return get<InfluencerDetail>({
    path: `/api/v1/profile/influencer/${encodeURIComponent(influencerId)}`
  });
}

/** 查询全部达人品类。返回体原样交给页面。 */
export function getCreatorProductCategories() {
  return get<CreatorProductCategory[]>({
    path: '/api/v1/tiktok/category/queryAll'
  });
}

/** 分页查询达人广场。筛选条件放在 JSON 请求体，会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function getInfluencerPage(body: InfluencerPageBody, query: InfluencerPageQuery = {}) {
  return postJSON<InfluencerPage>({
    path: _buildQueryUrl('/api/v1/profile/influencer/page', query),
    params: body
  });
}

/** 查询一条邀约详情。shopId 必填。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function getCollaborationDetail(id: number, query: CollaborationDetailQuery) {
  return get<TargetCollaborationDetail>({
    path: `/api/v1/engagement/collaborations/${encodeURIComponent(String(id))}`,
    params: query
  });
}

/** 分页查询当前店铺可售商品。shopId、pageNum、pageSize 必填。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getCommerceProducts(query: CommerceProductsQuery) {
  return get<ShopProductPage>({
    path: '/api/v1/commerce/products',
    params: query
  });
}

/** 分页查询当前店铺的本地邀约。筛选与分页参数放在 JSON 请求体。返回体原样交给页面。 */
export function getCollaborationPage(body: CollaborationPageQuery = {}) {
  return postJSON<TargetCollaborationPage>({
    path: '/api/v1/engagement/collaborations/page',
    params: body
  });
}

/** 创建定向邀约。返回体原样交给页面。 */
export function createCollaboration(body: CreateCollaborationBody) {
  return postJSON<TargetCollaborationCreateResult>({
    path: '/api/v1/engagement/collaborations/add',
    params: body
  });
}

/** 批量把商品加入邀约。最多 10 条邀约。返回体原样交给页面。 */
export function addCollaborationProducts(body: AddCollaborationProductsBody) {
  return postJSON<CollaborationBatchResult>({
    path: '/api/v1/engagement/collaborations/batch/addProducts',
    params: body
  });
}

/** 把已勾选的进行中邀约合并进主邀约。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function mergeCollaborations(body: MergeCollaborationsBody, query: MergeCollaborationsQuery = {}) {
  return postJSON<TargetCollaborationMergeResult>({
    path: _buildQueryUrl('/api/v1/engagement/collaborations/merge', query),
    params: body
  });
}

/** 批量取消邀约计划。最多 10 条本地邀约。返回体原样交给页面。 */
export function cancelCollaborations(body: CancelCollaborationsBody) {
  return postJSON<CollaborationBatchResult>({
    path: '/api/v1/engagement/collaborations/batch/cancel',
    params: body
  });
}

/** 批量移除邀约中的达人。最多 10 条邀约。返回体原样交给页面。 */
export function removeCollaborationCreators(body: RemoveCollaborationCreatorsBody) {
  return postJSON<CollaborationBatchResult>({
    path: '/api/v1/engagement/collaborations/batch/removeCreators',
    params: body
  });
}

/** 批量把达人加入邀约。最多 10 条邀约。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function addCollaborationCreators(
  body: AddCollaborationCreatorsBody,
  query: AddCollaborationCreatorsQuery = {}
) {
  return postJSON<CollaborationBatchResult>({
    path: _buildQueryUrl('/api/v1/engagement/collaborations/batch/addCreators', query),
    params: body
  });
}

/** 分页查询当前店铺的话术模板。shopId 必填；pageNum/pageSize/channel 按需传入。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function getContentTemplates(query: TemplateListQuery) {
  return get<TemplatePage>({
    path: '/api/v1/content/templates',
    params: query
  });
}

/** 查询话术模板详情。路径参数为模板 ID，shopId 必填。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getContentTemplateDetail(id: number, query: TemplateDetailQuery) {
  return get<TemplateDetail>({
    path: `/api/v1/content/templates/${encodeURIComponent(String(id))}`,
    params: query
  });
}

/** 新建话术模板。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function createContentTemplate(body: CreateTemplateBody, query: CreateTemplateQuery = {}) {
  return postJSON<TemplateDetail>({
    path: _buildQueryUrl('/api/v1/content/templates', query),
    params: body
  });
}

/** 编辑话术模板。路径参数为模板 ID；会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function updateContentTemplate(
  id: number,
  body: UpdateTemplateBody,
  query: UpdateTemplateQuery = {}
) {
  return putJSON<TemplateDetail>({
    path: _buildQueryUrl(`/api/v1/content/templates/${encodeURIComponent(String(id))}`, query),
    params: body
  });
}

/** 删除话术模板。路径参数为模板 ID；shopId 必填。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function deleteContentTemplate(id: number, query: DeleteTemplateQuery) {
  return deleteRequest<null>({
    path: _buildQueryUrl(`/api/v1/content/templates/${encodeURIComponent(String(id))}`, query)
  });
}

/** 一次性生成话术，不保留对话。channel 必填。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function generateContentTemplate(body: GenerateTemplateBody, query: GenerateTemplateQuery = {}) {
  return postJSON<GeneratedContentTemplate | string>({
    path: _buildQueryUrl('/api/v1/content/templates/ds-generate', query),
    params: body
  });
}

/** 分页查询当前用户在当前店铺已发过邮件的达人会话。shopId 必填。不传 status 表示全部。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getEmailConversations(query: EmailConversationListQuery) {
  return get<EmailConversationPage>({
    path: '/api/v1/email/conversations',
    params: query
  });
}

/** 统计当前店铺待商家回复的邮件会话数：status=NEED_REPLY 且未删除。shopId 必填。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getEmailPendingReplyCount(query: EmailPendingReplyCountQuery) {
  return get<number>({
    path: '/api/v1/email/conversations/pendingReplyCount',
    params: query
  });
}

/** 查询当前店铺本月已发、未读和待回复邮件数。shopId 必填。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getEmailStats(query: EmailStatsQuery) {
  return get<EmailInboxStats>({
    path: '/api/v1/email/stats',
    params: query
  });
}

/** 查询一条达人邮件会话的联系人、统计和消息时间线。路径使用会话 ID，shopId 必填。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getEmailConversationDetail(conversationId: string, query: EmailConversationDetailQuery) {
  return get<EmailConversationDetail>({
    path: `/api/v1/email/conversations/${encodeURIComponent(conversationId)}`,
    params: query
  });
}

/** 在会话里回复达人邮件。路径使用会话 ID。正文 text 与 html 至少填一项。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function replyEmailConversation(conversationId: string, body: ReplyEmailBody, query: ReplyEmailQuery = {}) {
  return postJSON<EmailSendResult>({
    path: _buildQueryUrl(`/api/v1/email/conversations/${encodeURIComponent(conversationId)}/reply`, query),
    params: body
  });
}

/** 把邮件会话标为已读。路径使用会话 ID。清空商家未读；若最后一条是入站消息，会话状态改为已处理。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function markEmailConversationRead(conversationId: string, query: ReplyEmailQuery = {}) {
  return postJSON<null>({
    path: _buildQueryUrl(`/api/v1/email/conversations/${encodeURIComponent(conversationId)}/read`, query)
  });
}

/** 创建邮件发送任务。返回体原样交给页面。 */
export function createEmailCampaign(body: CreateEmailCampaignBody) {
  return postJSON<EmailCampaign>({
    path: '/api/v1/email/campaigns',
    params: body
  });
}

/** 统计当前店铺待商家回复的私信数：未删除且最后一条为达人发送。shopId 必填。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getDmPendingReplyCount(query: DmPendingReplyCountQuery) {
  return get<number>({
    path: '/api/v1/dm/conversations/pendingReplyCount',
    params: query
  });
}

/** 拉取平台私信会话并交给服务端写入本地。shopId 必填，onlyNeedConversationId 固定为 false。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getDmConversations(query: DmConversationListQuery) {
  return get<DmConversationPage>({
    path: '/api/v1/dm/conversations',
    params: { ...query, onlyNeedConversationId: false }
  });
}

/** 拉取一条私信会话的消息。路径使用会话的 platformConversationId。shopId 必填。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getDmMessages(platformConversationId: string, query: DmMessageListQuery) {
  return get<DmMessagePage>({
    path: `/api/v1/dm/conversations/${encodeURIComponent(platformConversationId)}/messages`,
    params: query
  });
}

/** 把私信会话标为星标或取消星标。路径使用 platformConversationId。shopId 必填，starred 放入查询参数。会话参数仅在传入时发送。返回体原样交给页面。 */
export function starDmConversation(platformConversationId: string, query: StarDmConversationQuery) {
  return put<DmConversation>({
    path: _buildQueryUrl(`/api/v1/dm/conversations/${encodeURIComponent(platformConversationId)}/star`, query)
  });
}

/** 创建私信群发任务。任务进入队列，不立即调用平台发送。shopId、body、creators 放在请求体。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function createDmCampaign(body: CreateDmCampaignBody, query: CreateDmCampaignQuery = {}) {
  return postJSON<DmCampaignCreated>({
    path: _buildQueryUrl('/api/v1/dm/campaigns', query),
    params: body
  });
}

/** 分页查询当前用户在当前店铺的私信群发任务。shopId 必填。不传 status 表示全部。会话参数仅在传入时发送。返回体原样交给页面。 */
export function getDmCampaigns(query: DmCampaignListQuery) {
  return get<DmCampaignPage>({
    path: '/api/v1/dm/campaigns',
    params: query
  });
}

/** 在私信会话里发送一条消息。路径使用 platformConversationId。shopId、content 放在请求体。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function sendDmMessage(
  platformConversationId: string,
  body: SendDmMessageBody,
  query: SendDmMessageQuery = {}
) {
  return postJSON<DmSendResult>({
    path: _buildQueryUrl(`/api/v1/dm/conversations/${encodeURIComponent(platformConversationId)}/messages`, query),
    params: body
  });
}

/** 把私信会话标为已读。路径使用 platformConversationId。shopId 必填。会话参数仅在传入时发送。返回体原样交给页面。 */
export function markDmConversationRead(platformConversationId: string, query: ReadDmConversationQuery) {
  return put<DmConversation>({
    path: _buildQueryUrl(
      `/api/v1/dm/conversations/${encodeURIComponent(platformConversationId)}/read`,
      query
    )
  });
}

/** 存档或恢复私信会话。路径使用 platformConversationId。shopId 必填，archived 放入查询参数。会话参数仅在传入时发送。返回体原样交给页面。 */
export function archiveDmConversation(platformConversationId: string, query: ArchiveDmConversationQuery) {
  return put<DmConversation>({
    path: _buildQueryUrl(`/api/v1/dm/conversations/${encodeURIComponent(platformConversationId)}/archive`, query)
  });
}

/** 创建或取回达人私信会话。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function createDmConversation(body: CreateDmConversationBody, query: CreateDmConversationQuery = {}) {
  return postJSON<DmConversation>({
    path: _buildQueryUrl('/api/v1/dm/conversations', query),
    params: body
  });
}

/** 获取商家授权链接，由页面跳转 authorizationUrl */
export function createSellerAuthorizationUrl(body: { region: string; userCode: string }) {
  return postJSON<SellerAuthLink>({
    path: '/api/v1/auth/seller/authorization-url',
    params: body
  });
}

/** 解绑店铺授权，只把授权标记为已取消，不删除历史。会话参数仅在传入时作为查询参数。返回体原样交给页面。 */
export function unbindSellerShop(body: UnbindSellerShopBody, query: UnbindSellerShopQuery = {}) {
  return postJSON<null>({
    path: _buildQueryUrl('/api/v1/auth/seller/shops/unbind', query),
    params: body
  });
}

export type ChangePasswordBody = {
  oldPassword: string;
  newPassword: string;
};

/** 修改当前登录账号密码。成功后旧会话失效。返回体原样交给页面。 */
export function changePassword(body: ChangePasswordBody) {
  return postJSON<null>({
    path: '/api/v1/auth/password/change',
    params: body
  });
}

export type ResetPasswordBody = {
  identity: string;
  code: string;
  newPassword: string;
};

/** 通过验证码重置密码（未登录）。返回体原样交给页面。 */
export function resetPassword(body: ResetPasswordBody) {
  return postJSON<null>({
    path: '/api/v1/auth/password/reset',
    params: body
  });
}
